import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Alert,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useSetupLoginMutation } from '../../api/userManagementApi';
import type { UserManagementModal } from '../../commands/types';

type AddLoginDialogProps = {
  open: boolean;
  modal: UserManagementModal | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

export const AddLoginDialog = ({
  open,
  modal,
  onClose,
  onSuccess,
  onError,
}: AddLoginDialogProps) => {
  const availableRoles = modal?.availableRoles ?? [];

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [enable2Fa, setEnable2Fa] = useState(false);
  const [setupLogin, { isLoading }] = useSetupLoginMutation();

  const handleSubmit = async () => {
    if (!modal) return;
    if (selectedRoles.length === 0) {
      onError('Please select at least one role');
      return;
    }

    try {
      const res = await setupLogin({
        nodeKey: modal.nodeKey,
        keys: modal.relationKeys,
        roles: selectedRoles.join(','),
        enable2Fa,
        useSpaActivation: true,
      }).unwrap();

      if (!res.success) {
        onError(res.message);
        return;
      }

      onSuccess(res.message);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to create login');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Login</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {modal?.availableRolesWarning && (
            <Alert severity="warning">{modal.availableRolesWarning}</Alert>
          )}
          <Autocomplete
            multiple
            options={availableRoles}
            value={selectedRoles}
            disabled={availableRoles.length === 0}
            onChange={(_e, value) => setSelectedRoles(value)}
            renderInput={(params) => (
              <TextField {...params} label="Roles" placeholder="Select roles" />
            )}
          />
          <FormControlLabel
            control={
              <Checkbox checked={enable2Fa} onChange={(e) => setEnable2Fa(e.target.checked)} />
            }
            label="Enable two-factor authentication"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || availableRoles.length === 0}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLoginDialog;
